import { cn } from '@/lib/utils';

interface SkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  width?: string | number;
  height?: string | number;
  rounded?: boolean;
}

/**
 * Base skeleton component for loading states
 * Provides consistent animation and theming across the application
 */
function Skeleton({
  className,
  width,
  height,
  rounded = false,
  ...props
}: SkeletonProps) {
  return (
    <div
      className={cn(
        'animate-pulse bg-gray-200 dark:bg-gray-700',
        rounded ? 'rounded-full' : 'rounded-md',
        className
      )}
      style={{
        width: typeof width === 'number' ? `${width}px` : width,
        height: typeof height === 'number' ? `${height}px` : height
      }}
      {...props}
    />
  );
}

/**
 * Skeleton text line - matches typical text height and spacing
 */
function SkeletonText({ 
  className, 
  width = "100%" 
}: { 
  className?: string; 
  width?: string | number; 
}) {
  return (
    <Skeleton 
      className={cn("h-4", className)} 
      width={width} 
    />
  );
}

/**
 * Skeleton heading - taller than regular text
 */
function SkeletonHeading({ 
  className, 
  width = "60%" 
}: { 
  className?: string; 
  width?: string | number; 
}) {
  return (
    <Skeleton 
      className={cn("h-6", className)} 
      width={width} 
    />
  );
}

/**
 * Skeleton card - common card dimensions with image and text
 */
function SkeletonCard({ className }: { className?: string }) {
  return (
    <div className={cn("space-y-3", className)}>
      <Skeleton className="h-48 w-full" />
      <div className="space-y-2">
        <SkeletonHeading />
        <SkeletonText />
        <SkeletonText width="80%" />
      </div>
    </div>
  );
}

/**
 * Skeleton avatar - circular profile image placeholder
 */
function SkeletonAvatar({ 
  size = 40,
  className 
}: { 
  size?: number;
  className?: string;
}) {
  return (
    <Skeleton 
      className={cn("rounded-full", className)}
      width={size}
      height={size}
    />
  );
}

/**
 * Skeleton button - common button dimensions
 */
function SkeletonButton({ 
  className,
  width = 100
}: { 
  className?: string;
  width?: number;
}) {
  return (
    <Skeleton 
      className={cn("h-10 rounded-md", className)}
      width={width}
    />
  );
}

export {
  Skeleton,
  SkeletonText,
  SkeletonHeading,
  SkeletonCard,
  SkeletonAvatar,
  SkeletonButton
};
